/**
 * Initials entry — single-source charset, sanitization, and cycling.
 * Pure functions for player name setup with leaderboard-safe constraints.
 */

/**
 * The ONLY allowed initials character set — leaderboard-safe. LOCKED — ship this exact set, do not edit it.
 * A–Z, 0–9, space, and the curated punctuation .-_!?* — nothing else. DELIBERATELY excludes
 * HTML/markup-significant chars (< > & " ' / \ `) so the public board never gains an injection/spoofing
 * surface. This set is a locked decision (D-126); do not add or remove characters.
 */
export const INITIALS_CHARSET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 .-_!?*";

/** Exactly 3 characters for player initials. */
export const INITIALS_LENGTH = 3;

/** Safe default when a slot is empty. */
export const INITIALS_FALLBACK = "A";

/** Canonical default initials used across UI/persistence/systems. */
export const DEFAULT_INITIALS = INITIALS_FALLBACK.repeat(INITIALS_LENGTH);

/**
 * Check if a character is allowed in initials (membership in INITIALS_CHARSET, case-insensitive).
 */
export function isAllowedInitialChar(ch: string): boolean {
  if (!ch || ch.length !== 1) return false;
  const upper = ch.toUpperCase();
  return INITIALS_CHARSET.includes(upper);
}

/**
 * Sanitize raw initials: uppercase → filter to charset → take INITIALS_LENGTH → pad with INITIALS_FALLBACK.
 * Guarantees output is exactly INITIALS_LENGTH chars, all from INITIALS_CHARSET.
 */
export function sanitizeInitials(raw: string): string {
  if (!raw) return DEFAULT_INITIALS;

  // Uppercase, filter to allowed chars
  const upper = raw.toUpperCase();
  const filtered = Array.from(upper)
    .filter((ch) => INITIALS_CHARSET.includes(ch))
    .join("");

  // Take first INITIALS_LENGTH chars
  const truncated = filtered.slice(0, INITIALS_LENGTH);

  // Pad with INITIALS_FALLBACK to reach INITIALS_LENGTH
  return (truncated + DEFAULT_INITIALS).slice(0, INITIALS_LENGTH);
}

/**
 * Prefill policy for an EXTERNAL name source seeding the callsign (today: the
 * Game Center alias on iOS; owner decision 2026-08-18). The initials are the
 * in-fiction Operator callsign and stay player-owned — a platform identity may
 * seed them exactly once, never overwrite them:
 *
 * - Returns the sanitized 3-char candidate ONLY while `current` is still
 *   {@link DEFAULT_INITIALS} (the codebase-wide "player has not chosen yet"
 *   sentinel — PreRunScreenInput uses the same test for its "known" state).
 * - Returns null when the player already chose, when the source sanitizes to
 *   the default anyway (nothing would change), or when the source is empty —
 *   null means "do not touch, do not persist".
 */
export function prefillInitials(
  current: string,
  source: string
): string | null {
  if (current !== DEFAULT_INITIALS) return null;
  const candidate = sanitizeInitials(source);
  return candidate === current ? null : candidate;
}

/**
 * Cycle a character forward or backward within INITIALS_CHARSET.
 * @param current The current character (must be uppercase or in INITIALS_CHARSET)
 * @param dir 1 for forward, -1 for backward
 * @returns The next/previous character, wrapping around
 */
export function cycleInitialChar(current: string, dir: 1 | -1): string {
  const upper = current.toUpperCase();
  if (!INITIALS_CHARSET.includes(upper)) {
    // Fallback: if invalid, return first char of charset
    return INITIALS_CHARSET[0] ?? INITIALS_FALLBACK;
  }

  const idx = INITIALS_CHARSET.indexOf(upper);
  const len = INITIALS_CHARSET.length;
  const nextIdx = (idx + dir + len) % len;
  return INITIALS_CHARSET[nextIdx] ?? INITIALS_FALLBACK;
}
